import { TFunction } from "i18next";
import {
	tipoCreditoSchema,
	periodoTasaSchema,
	tipoSeguroSchema,
} from "@/schemas/common";
import { coberturaSeguroDesgravamenSchema } from "../schemas/mortgage";

/**
 * Month keys for i18n lookup
 */
const MONTH_KEYS = [
	"january",
	"february",
	"march",
	"april",
	"may",
	"june",
	"july",
	"august",
	"september",
	"october",
	"november",
	"december",
] as const;

/**
 * Bank options - hardcoded list of Peruvian financial institutions
 * Note: These don't need i18n as bank names are the same in all languages
 */
export const BANK_OPTIONS = [
	{ value: "BCP", label: "BCP" },
	{ value: "Interbank", label: "Interbank" },
	{ value: "BBVA", label: "BBVA" },
	{ value: "Scotiabank", label: "Scotiabank" },
	{ value: "BanBif", label: "BanBif" },
	{ value: "Banco de la Nación", label: "Banco de la Nación" },
	{ value: "MiBanco", label: "MiBanco" },
	{ value: "GNB", label: "GNB" },
	{ value: "Banco Pichincha", label: "Banco Pichincha" },
	{ value: "Otro", label: "Otro" },
];

/**
 * Credit type options for the "¿Crédito MiVivienda?" input.
 * Solo dos opciones: Sí → mi_vivienda, No → tradicional.
 */
export const getCreditTypeOptions = (t: TFunction) => [
	{ value: tipoCreditoSchema.enum.mi_vivienda, label: t("common:yes") },
	{ value: tipoCreditoSchema.enum.tradicional, label: t("common:no") },
];

/**
 * Generate translated period options (for interest rate, insurance)
 * Excludes 'diaria' as it's not commonly used in mortgage forms
 */
export const getPeriodOptions = (t: TFunction, includeDiaria = false) =>
	periodoTasaSchema.options
		.filter((p) => includeDiaria || p !== "diaria")
		.map((value) => ({
			value,
			label: t(`hipotecas:create.period.${value}`),
		}));

/**
 * Generate translated insurance type options
 */
export const getInsuranceTypeOptions = (t: TFunction) =>
	tipoSeguroSchema.options.map((value) => ({
		value,
		label: t(`hipotecas:create.insurance_type.${value}`),
	}));

/**
 * Generate translated desgravamen coverage options
 */
export const getCoverageOptions = (t: TFunction) =>
	coberturaSeguroDesgravamenSchema.options.map((value) => ({
		value,
		label: t(`hipotecas:create.form.life_insurance_coverage.${value}`),
	}));

/**
 * Generate translated month options for double payment selection
 */
export const getMonthOptions = (t: TFunction, language: string = "es") =>
	MONTH_KEYS.map((key, index) => {
		const date = new Date(2024, index, 15); // Use mid-month to avoid timezone edge cases
		const shortText = new Intl.DateTimeFormat(language, {
			month: "short",
		}).format(date);
		// Capitalize first letter
		const capitalizedShortText =
			shortText.charAt(0).toUpperCase() + shortText.slice(1);

		return {
			value: String(index + 1),
			text: t(`common:months.${key}`),
			shortText: capitalizedShortText,
		};
	});

/**
 * Generate cuotas options (regular vs double payments)
 */
export const getCuotasOptions = (t: TFunction) => [
	{ value: "regular", label: t("hipotecas:create.form.regular_payments") },
	{ value: "doble", label: t("hipotecas:create.form.double_payments") },
];
